import Button from "common/Buttons/Button";
import BotConfigurationModal from "features/bots/BotForm/common/BotConfigurationModal";
import { ONBOARDING_BOT_CONFIGURATION_ID } from "testIds";
import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { OnboardingStyles } from "features/onboarding/common/styles";
import useMedia from "utils/hooks/useMedia";
import { BASE_DCA_BOTS_PATH, MOBILE_SEARCH } from "common/constants";
import SkipOnboardingModal from "features/onboarding/common/SkipOnboardingModal";
import {
  SKIP_ONBOARDING_DCA_BOT,
  TRADING_METHOD_TO_SKIP_CREATE_BOT,
} from "@shared/features/onboarding/constants";
import TextWithIcon from "common/Text/TextWithIcon";
import { ICON_TYPES } from "@shared/common/constants";
import Flex from "common/Boxes/Flex";
import AccountsList from "features/onboarding/overview/accountsList/AccountsList";
import styled, { useTheme } from "styled-components";
import PublicBotCardMainInfo from "features/bots/BotForm/General/PublicBotCardMainInfo";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Text from "common/Typography/Text";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import SaveBotButton from "features/bots/common/SaveBotButton";
import PhoneActionBar from "features/onboarding/common/PhoneActionBar";
import {
  AmountPerTradeType,
  DcaBotCreateeditOrigin,
  OnboardingStep,
  TradingMethodCategoryValueType,
  TradingMethodType as AnalyticsTradingMethodType,
} from "analytics/events";
import Link from "common/Typography/Link";
import useBotOverview from "@shared/features/onboarding/hooks/useBotOverview";
import {
  getAnalyticsBotSource,
  openMessage,
  startIntercomChecklist,
} from "utils/misc";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import {
  OnboardingDetailedStep,
  TradingMethodType,
} from "@shared/features/onboarding/types";
import { useBottomNavigator, useIsOnboarding } from "utils/hooks/misc";
import { isSymbol } from "@shared/types/utils";
import useQuery from "utils/hooks/useQuery";
import {
  BOT_RANK_QUERY_PARAM,
  NEW_BOT_ID_QUERY_PARAM,
} from "features/bots/common/constants";
import { BotData, LanguageNameSpaces, WorkspaceType } from "@shared/types";
import { useSelector } from "react-redux";
import { selectWorkspace } from "@shared/slices/common.slice";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  ONBOARDING_CUSTOMIZE_ID,
  ONBOARDING_SKIP_CREATE_BOT_BUTTON_ID,
} from "testIds.ts";
import LinkButton from "common/Buttons/LinkButton";

interface BotOverviewCardProps {
  onCreated?: () => void;
  tradingMethodCategory?: TradingMethodCategoryValueType;
}

const BotOverviewCard = ({
  tradingMethodCategory,
  onCreated,
}: BotOverviewCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const theme = useTheme();
  const { t: translateBots } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const [configurationOpen, setConfigurationOpen] = useState(false);
  const { analytics } = useContext(AnalyticsContext);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const [skipOnboardingModalVisible, setSkipOnboardingModalVisible] =
    useState(false);
  const isOnboarding = useIsOnboarding();
  const query = useQuery();
  const isDemo = useSelector(selectWorkspace) === WorkspaceType.DEMO;
  const botRank = query.get(BOT_RANK_QUERY_PARAM) ?? undefined;
  const parsedBotRank = botRank !== undefined ? parseInt(botRank) : undefined;
  const bottomNavigatorData = useBottomNavigator();

  const { onFinishOnboarding, finishOnboardingLoading, changeStep } =
    useContext(OnboardingContext)!;
  // The full form for this preset (a copy of it), for those who want to
  // change more than the amount.
  const customize = () =>
    changeStep(OnboardingDetailedStep.TRADING_METHOD, {
      setup: "1",
      preset: "",
      method: "dca",
    });

  const {
    publicBotGeneralData,
    botId,
    botName,
    botSymbol,
    amountValueData,
    minAmountErrorMessage,
    maxBotAmountWarningMessage,
    amountPerTradeHelperTooltip,
    isLoading,
    saveButtonDisabled,
    accountExchange,
    amountValuesChanged,
  } = useBotOverview(openMessage, isOnboarding);

  const botSource = useMemo(() => getAnalyticsBotSource(), []);
  const analyticsProps = {
    onboardingStep: OnboardingStep.OVERVIEW,
    tradingMethodType: AnalyticsTradingMethodType.DCA_BOT,
    clientExchange: accountExchange || "",
    copiedBotLeverage: publicBotGeneralData?.leverage,
    copiedBotSymbol: botSymbol,
    marketplaceGroupId: undefined,
    groupName: undefined,
    tradingMethodCategory,
    botSource,
    botRank: parsedBotRank,
    marketplaceGroupPagePosition: undefined,
  };

  // Once the bot has loaded, so its exchange and symbol are known.
  const overviewSent = useRef(false);
  useEffect(() => {
    if (!analytics || isLoading || overviewSent.current) return;
    overviewSent.current = true;
    analytics.onboardingOverviewView(analyticsProps);
  }, [analytics, isLoading]);
  // When the warning shows, not on the later save.
  const warningSent = useRef(false);
  useEffect(() => {
    if (!analytics || !maxBotAmountWarningMessage || warningSent.current)
      return;
    warningSent.current = true;
    analytics.onboardingStartTradingWarningView({
      onboardingStep: OnboardingStep.OVERVIEW,
      amountPerTradeType: AmountPerTradeType.FIXED_USD,
      tradingMethodType: AnalyticsTradingMethodType.DCA_BOT,
      tradingMethodCategory,
    });
  }, [analytics, maxBotAmountWarningMessage]);

  const onSaveClicked = () => {
    analytics?.onboardingStartTradingClicked({
      ...analyticsProps,
      amountPerTradeType: AmountPerTradeType.FIXED_USD,
      marketplaceGroupOffersFreeTrial: undefined,
      signalsAutoTradingConfiguration: undefined,
      amountPerTradeIsDefault: !amountValuesChanged,
    });
  };

  const finishAndViewBot = (botData?: BotData): void => {
    const queryToAppend = botData
      ? {
          [NEW_BOT_ID_QUERY_PARAM]: botData.id.toString(),
        }
      : undefined;
    onFinishOnboarding(BASE_DCA_BOTS_PATH, queryToAppend);
    if (!isDemo && !bottomNavigatorData.baseShouldDisplay) {
      startIntercomChecklist(27892453);
    }
  };

  const onSuccess = (botData?: BotData): void => {
    if (!botData?.id) return;
    if (isDemo) {
      onCreated?.();
    }
    finishAndViewBot(botData);
  };

  const symbolRow = (
    <SymbolWrapper>
      <OnboardingStyles.OverviewTitle style={{ margin: 0 }}>
        {t("bot_overview.symbol")}
      </OnboardingStyles.OverviewTitle>
      <TextWithIcon
        text={botSymbol}
        iconType={ICON_TYPES.COIN}
        coinOrExchangeName={botSymbol}
        iconSize="l"
        textStyle={{ color: theme.colors.primaryText }}
      />
    </SymbolWrapper>
  );

  const amountInput = (
    <div style={{ marginTop: 24 }}>
      <LoadingSpinnerOverlay isLoading={!amountValueData}>
        <OnboardingStyles.OverviewTitle style={{ display: "flex" }}>
          {t("bot_overview.amount_per_trade")}
          <HelperTooltip {...amountPerTradeHelperTooltip} />
        </OnboardingStyles.OverviewTitle>
        <AmountWrapper
          $error={!!minAmountErrorMessage}
          $warning={!!maxBotAmountWarningMessage}
        >
          <OnboardingStyles.Input
            value={amountValueData.value}
            placeholder={amountValueData.placeholder}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              amountValueData.onChange(e.target.value)
            }
            suffix={<Text type="secondary">{amountValueData.suffix}</Text>}
            variant="borderless"
          />
        </AmountWrapper>
        {minAmountErrorMessage && (
          <OnboardingStyles.ErrorText $isMobile={isMobile}>
            {minAmountErrorMessage}
          </OnboardingStyles.ErrorText>
        )}
        {maxBotAmountWarningMessage && !minAmountErrorMessage && (
          <OnboardingStyles.WarningText $isMobile={isMobile}>
            {maxBotAmountWarningMessage}
          </OnboardingStyles.WarningText>
        )}
      </LoadingSpinnerOverlay>
    </div>
  );

  const buttons = (
    <ButtonsWrapper>
      <PhoneActionBar busy={isLoading}>
        <SaveBotButton
          actionLabel={
            isDemo ? t("guided.start_demo_bot") : t("guided.start_live_bot")
          }
          copiedFrom={botId}
          disabled={saveButtonDisabled}
          analyticsBotOriginType={DcaBotCreateeditOrigin.COPY_AS_IS}
          errorMessage={t("bot_overview.resolve_issues_error")}
          buttonSize="large"
          style={{ margin: "16px 0" }}
          onSuccess={onSuccess}
          onSaveClick={onSaveClicked}
        />
        {isDemo && (
          <CustomizeLink
            type="link"
            onClick={customize}
            data-testid={ONBOARDING_CUSTOMIZE_ID}
          >
            {t("intent.confirm_customize")}
          </CustomizeLink>
        )}
      </PhoneActionBar>
      <Link
        onClick={() => setSkipOnboardingModalVisible(true)}
        data-testid={ONBOARDING_SKIP_CREATE_BOT_BUTTON_ID}
      >
        {t(TRADING_METHOD_TO_SKIP_CREATE_BOT[TradingMethodType.DCA_BOT])}
      </Link>
    </ButtonsWrapper>
  );

  const modal = (
    <SkipOnboardingModal
      isVisible={skipOnboardingModalVisible}
      content={SKIP_ONBOARDING_DCA_BOT}
      onCancel={() => setSkipOnboardingModalVisible(false)}
      newPath={"/"}
      onboardingStep={OnboardingStep.OVERVIEW}
      tradingMethodType={AnalyticsTradingMethodType.DCA_BOT}
    />
  );

  return (
    <OnboardingStyles.OverviewCard $isMobile={isMobile}>
      <LoadingSpinnerOverlay
        isLoading={isLoading || finishOnboardingLoading}
        hideContentWhileLoading={isLoading}
        wrap={true}
        backgroundColor={theme.colors.cardBgColor}
        // The bar sits outside this overlay: hold the card's height only
        // while loading, so the loaded card keeps its own spacing.
        wrapStyle={isLoading ? { minHeight: 400 } : undefined}
        spinnerSize="large"
      >
        {!isSymbol(botName) && <PublicBotCardMainInfo name={botName} />}
        {isDemo && (
          <Text style={{ marginTop: 12 }}>{t("guided.virtual_only")}</Text>
        )}
        {symbolRow}
        <AccountsList
          symbol={botSymbol ?? publicBotGeneralData?.symbols?.[0]}
          isBot={true}
          isOnboarding={true}
          preferredExchange={publicBotGeneralData?.exchange ?? null}
        />
        <Button
          type="link"
          style={{ paddingLeft: 0, marginTop: 12 }}
          onClick={() => setConfigurationOpen(true)}
          data-testid={ONBOARDING_BOT_CONFIGURATION_ID}
        >
          {translateBots("buttons.show_bot_configuration")}
        </Button>
        <BotConfigurationModal
          bot={publicBotGeneralData}
          open={configurationOpen}
          closeModal={() => setConfigurationOpen(false)}
        />
        {amountInput}
        {modal}
      </LoadingSpinnerOverlay>
      {/* Outside the loading overlay: on phones the bar is there from the
          first frame (inactive until ready), so nothing shifts later. */}
      {buttons}
    </OnboardingStyles.OverviewCard>
  );
};

export default BotOverviewCard;

const SymbolWrapper = styled(Flex)`
  border-bottom: ${(p) => p.theme.borders.lightest};
  padding-bottom: 12px;
  margin: 20px 0 12px 0;
`;
const AmountWrapper = styled(Flex)<{ $error: boolean; $warning: boolean }>`
  border: ${(p) => p.theme.borders.light};
  border-radius: ${(p) => p.theme.radii.control}px;
  border-color: ${(p) =>
    p.$error
      ? p.theme.colors.red
      : p.$warning
      ? p.theme.colors.gold
      : p.theme.colors.inputBorderColor};
  width: 100%;
  padding: 2px;
`;
const ButtonsWrapper = styled.div`
  text-align: center;
  /* Phones: the actions are in the bottom bar; the skip link keeps its
     distance from the amount. */
  @media (max-width: 560px) {
    padding-top: 16px;
  }
`;
const CustomizeLink = styled(LinkButton)`
  display: block;
  margin: -8px auto 12px;
`;
